import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Check,
  Calculator,
  FileText,
  LayoutDashboard,
  ArrowRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
} from 'lucide-react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { MapleLeaf } from '@/components/ui/MapleLeaf'

interface UserData {
  email: string
  exchange: string
  otherExchange?: string
  featureText: string
  votedFeatures: string[]
  position: number
  editCount: number
  lastEditAt?: string
}

const FEATURE_OPTIONS = [
  {
    id: 'acb',
    label: 'Perfect ACB + superficial loss rule',
    description: 'Automatic cost basis tracking with superficial loss detection',
    icon: Calculator,
    color: 'from-emerald-500 to-teal-600',
  },
  {
    id: 'tax',
    label: 'One-click CRA tax reports',
    description: 'Generate Schedule 3 and complete tax forms instantly',
    icon: FileText,
    color: 'from-amber-500 to-orange-600',
  },
  {
    id: 'dashboard',
    label: 'Beautiful real-time dashboard',
    description: 'Track your portfolio with live prices and performance',
    icon: LayoutDashboard,
    color: 'from-violet-500 to-purple-600',
  },
]

export function EditPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [userData, setUserData] = useState<UserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [featureText, setFeatureText] = useState('')
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [rateLimited, setRateLimited] = useState(false)
  const [waitMinutes, setWaitMinutes] = useState(0)

  // Fetch user data on mount
  useEffect(() => {
    if (!token) {
      navigate('/')
      return
    }

    const fetchData = async () => {
      try {
        const response = await fetch(`/api/edit/${token}`)
        const data = await response.json()

        if (!response.ok) {
          if (response.status === 404) {
            setError('This link is invalid or has expired.')
          } else if (response.status === 410) {
            setError('This link has expired. Please sign up again.')
          } else {
            setError(data.error || 'Failed to load your data.')
          }
          return
        }

        setUserData(data)
        setFeatureText(data.featureText || '')
        setSelectedFeatures(data.votedFeatures || [])
      } catch {
        setError('Unable to connect. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [token, navigate])

  const toggleFeature = (id: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    )
  }

  const handleSubmit = async () => {
    if (!token) return

    setIsSubmitting(true)
    setRateLimited(false)

    try {
      const response = await fetch(`/api/edit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          featureText,
          votedFeatures: selectedFeatures,
        }),
      })

      const data = await response.json()

      if (response.status === 429) {
        setRateLimited(true)
        setWaitMinutes(data.waitMinutes || 60)
        return
      }

      if (!response.ok) {
        throw new Error(data.error)
      }

      setIsSubmitted(true)
      setUserData((prev) =>
        prev ? { ...prev, editCount: data.editCount, featureText, votedFeatures: selectedFeatures } : null
      )

      // Reset success state after a delay
      setTimeout(() => {
        setIsSubmitted(false)
      }, 5000)
    } catch {
      setError('Failed to save. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading your wish list...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-loss-500/20 rounded-full">
            <AlertCircle className="w-8 h-8 text-loss-400" />
          </div>
          <h1 className="text-2xl font-bold text-slate-100 mb-3">{error}</h1>
          <p className="text-slate-400 mb-6">
            If you need a new link, please sign up again on our homepage.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gold-500 text-midnight-950 font-semibold rounded-xl hover:bg-gold-400 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    )
  }

  if (!userData) {
    return null
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px]
                     bg-gradient-radial from-gold-500/8 via-transparent to-transparent
                     blur-3xl"
        />

        <motion.div
          animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-24 right-[15%] opacity-10"
        >
          <MapleLeaf className="w-20 h-20 text-maple-500" />
        </motion.div>

        <div className="absolute inset-0 grid-pattern opacity-20" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            {/* Position Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6
                          bg-midnight-800/60 backdrop-blur-xl
                          border border-slate-700/40 rounded-full">
              <span className="text-slate-400 text-sm">Your position:</span>
              <span className="text-gold-400 font-bold font-mono">#{userData.position.toLocaleString()}</span>
              {userData.editCount > 0 && (
                <span className="text-slate-500 text-xs">
                  | Edited {userData.editCount} time{userData.editCount > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2">
              Update Your <span className="text-gold-400">Wish List</span>
            </h1>

            <p className="text-slate-400">
              Welcome back, <span className="text-slate-200">{userData.email}</span>
            </p>
          </motion.div>

          {/* Rate Limit Warning */}
          <AnimatePresence>
            {rateLimited && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <p className="text-amber-200 text-sm">
                    You can only edit once per hour. Please wait {waitMinutes} minutes.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Message */}
          <AnimatePresence>
            {isSubmitted && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-6"
              >
                <div className="p-4 bg-profit-500/10 border border-profit-500/30 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-profit-400 flex-shrink-0" />
                  <p className="text-profit-300 text-sm">
                    Your wish list has been updated! Keep refining your ideas.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-8"
          >
            {/* Open Text Question */}
            <div
              className="p-6 bg-midnight-800/40 backdrop-blur-lg
                        border border-slate-700/40 rounded-2xl"
            >
              <label className="block text-lg font-semibold text-slate-100 mb-4">
                What is the <span className="text-gold-400">#1 feature</span> you absolutely need?
              </label>
              <textarea
                value={featureText}
                onChange={(e) => setFeatureText(e.target.value)}
                placeholder="Tell us what would make this app a must-have for you..."
                rows={4}
                disabled={rateLimited}
                className={cn(
                  'w-full px-5 py-4 bg-midnight-900/60 border border-slate-700/50 rounded-xl',
                  'text-slate-100 placeholder-slate-500 text-base',
                  'focus:outline-none focus:border-gold-500/50 focus:ring-2 focus:ring-gold-500/20',
                  'transition-all duration-200 resize-none',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              />
            </div>

            {/* Feature Voting */}
            <div className="space-y-4">
              <p className="text-slate-400 text-center">Quick-vote on popular requests:</p>

              <div className="grid gap-4">
                {FEATURE_OPTIONS.map((feature) => {
                  const Icon = feature.icon
                  const isSelected = selectedFeatures.includes(feature.id)

                  return (
                    <button
                      key={feature.id}
                      onClick={() => !rateLimited && toggleFeature(feature.id)}
                      disabled={rateLimited}
                      className={cn(
                        'group relative flex items-center gap-4 p-5',
                        'bg-midnight-800/40 backdrop-blur-lg rounded-xl',
                        'border-2 transition-all duration-300',
                        'text-left w-full',
                        'disabled:opacity-50 disabled:cursor-not-allowed',
                        isSelected
                          ? 'border-gold-500/50 bg-gold-500/5'
                          : 'border-slate-700/40 hover:border-slate-600/50'
                      )}
                    >
                      <div
                        className={cn(
                          'flex-shrink-0 w-12 h-12 rounded-xl',
                          'flex items-center justify-center',
                          'bg-gradient-to-br',
                          feature.color,
                          'transition-transform duration-300',
                          isSelected ? 'scale-110' : 'group-hover:scale-105'
                        )}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3
                          className={cn(
                            'font-semibold text-base transition-colors',
                            isSelected ? 'text-gold-400' : 'text-slate-100'
                          )}
                        >
                          {feature.label}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">{feature.description}</p>
                      </div>

                      <div
                        className={cn(
                          'flex-shrink-0 w-6 h-6 rounded-md border-2',
                          'flex items-center justify-center transition-all',
                          isSelected
                            ? 'bg-gold-500 border-gold-500'
                            : 'border-slate-600 group-hover:border-slate-500'
                        )}
                      >
                        {isSelected && <Check className="w-4 h-4 text-midnight-950" strokeWidth={3} />}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-4">
              <motion.button
                onClick={handleSubmit}
                disabled={isSubmitting || rateLimited}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  'px-10 py-4 text-lg font-bold rounded-xl',
                  'bg-gradient-to-r from-gold-500 to-gold-400 text-midnight-950',
                  'shadow-lg shadow-gold-500/30',
                  'hover:shadow-xl hover:shadow-gold-500/40',
                  'transition-all duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg',
                  'flex items-center gap-3'
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    Update My Wish List
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>

            {/* Footer Note */}
            <p className="text-center text-sm text-slate-500">
              Come back anytime with this link to update your requests.
              <br />
              We're building this for <span className="text-slate-400">you</span>.
            </p>
          </motion.div>

          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12 text-center"
          >
            <a
              href="/"
              className="text-slate-500 hover:text-gold-400 transition-colors text-sm"
            >
              Back to TrackerCapital
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
